import { useEffect, useState } from "react";
import { Plus, Trash2, Umbrella, GraduationCap, ArrowUp, Check, X, Settings2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { fmtUK, todayISO } from "../../lib/utils";
import { YEAR_PRESETS, PRESET_LABELS } from "../../lib/yearPresets";
import CalendarView from "../../components/CalendarView";

export function CalendarPanel({ child, exams, isStudent, subscribed, reload }) {
  const [holidayForm, setHolidayForm] = useState({ name: "", start: "", end: "", kind: "closure" });
  const [examForm, setExamForm] = useState({ title: "", date: "", start_time: "", notes: "" });
  const [examPast, setExamPast] = useState(false);
  const [childYear, setChildYear] = useState(child.year_group || "");
  const [yearGroupsDraft, setYearGroupsDraft] = useState(child.year_groups || YEAR_PRESETS.scottish);
  const [newYear, setNewYear] = useState("");
  const [clearOnAdvance, setClearOnAdvance] = useState(false);

  useEffect(() => {
    setChildYear(child.year_group || "");
    setYearGroupsDraft(child.year_groups || YEAR_PRESETS.scottish);
    setExamPast(false);
  }, [child.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const holidays = child.holidays || [];
  const yourLC = isStudent ? "your" : `${child.name}'s`;

  const saveHolidays = async (next) => {
    try {
      await api.put(`/parent/children/${child.id}/holidays`, { holidays: next.map(({ id, name, start, end, kind }) => ({ id, name, start, end, kind: kind || "closure" })) });
      toast.success("Holidays saved");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addHoliday = () => {
    if (!holidayForm.name.trim() || !holidayForm.start || !holidayForm.end) return toast.error("Fill in the name and both dates");
    saveHolidays([...holidays, holidayForm]);
    setHolidayForm({ name: "", start: "", end: "", kind: "closure" });
  };

  const saveChildYear = async () => {
    try {
      await api.put(`/parent/children/${child.id}`, { year_group: childYear });
      toast.success("Year group saved");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const advance = async () => {
    try {
      const { data } = await api.post(`/parent/children/${child.id}/advance`, { clear: clearOnAdvance });
      toast.success(`Moved up to ${data.year_group}${clearOnAdvance ? " — timetable cleared for a fresh start" : ""}`);
      setClearOnAdvance(false);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const addExam = async () => {
    if (!examForm.title.trim() || !examForm.date) return toast.error("Add an exam name and date");
    if (!subscribed) return toast.error("Subscribe to add exams");
    if (examForm.date < todayISO() && !examPast) { setExamPast(true); return; }
    try {
      await api.post(`/parent/children/${child.id}/exams`, examForm);
      toast.success("Exam added to the calendar");
      setExamForm({ title: "", date: "", start_time: "", notes: "" });
      setExamPast(false);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteExam = async (id) => {
    try {
      await api.delete(`/parent/children/${child.id}/exams/${id}`);
      toast.success("Exam removed");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveYearGroups = async () => {
    try {
      await api.put(`/parent/children/${child.id}/year-groups`, { year_groups: yearGroupsDraft });
      toast.success("Year groups saved");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6 items-start" data-testid="parent-calendar-view">
      <div className="lg:col-span-3">
        <CalendarView holidays={holidays} exams={exams} weeks={child.schedule.weeks || 1} />
      </div>
      <div className="lg:col-span-2 space-y-4">
        <div className="nb-card p-5">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><Umbrella className="w-5 h-5" /> School holidays</h2>
          <p className="text-xs text-zinc-500 font-semibold mt-1">Add closed dates (summer, Easter, Christmas, inset days) and study days off so the calendar shows them.</p>
          <div className="mt-4 space-y-2" data-testid="parent-holidays-list">
            {holidays.map((h) => (
              <div key={h.id} className="flex items-center justify-between gap-2 rounded-xl border-2 border-zinc-900 p-2.5 bg-[#FDFBF7]" data-testid={`parent-holiday-${h.id}`}>
                <div>
                  <div className="font-bold text-sm">{h.name}</div>
                  <div className="text-[11px] text-zinc-500 font-semibold">
                    {new Date(h.start + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – {new Date(h.end + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                </div>
                <button onClick={() => subscribed ? saveHolidays(holidays.filter((x) => x.id !== h.id)) : toast.error("Subscribe to edit holidays")}
                  data-testid={`parent-holiday-delete-${h.id}`}
                  className="p-1.5 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150 shrink-0">
                  <Trash2 className="w-3.5 h-3.5 text-red-700" />
                </button>
              </div>
            ))}
            {holidays.length === 0 && <p className="text-sm text-zinc-400 font-semibold" data-testid="parent-holidays-empty">No holidays added yet.</p>}
          </div>
          <div className="mt-3 space-y-2">
            <input className="nb-input" placeholder="e.g. Summer holidays" data-testid="parent-holiday-name"
              value={holidayForm.name} onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })} maxLength={60} />
            <select className="nb-input" data-testid="parent-holiday-kind"
              value={holidayForm.kind} onChange={(e) => setHolidayForm({ ...holidayForm, kind: e.target.value })}>
              <option value="closure">School closed</option>
              <option value="study">Study day off</option>
            </select>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input type="date" className="nb-input" data-testid="parent-holiday-start"
                  value={holidayForm.start} onChange={(e) => setHolidayForm({ ...holidayForm, start: e.target.value })} />
                {holidayForm.start && <p className="text-[11px] text-zinc-400 font-semibold mt-1">{fmtUK(holidayForm.start)}</p>}
              </div>
              <div>
                <input type="date" className="nb-input" data-testid="parent-holiday-end"
                  value={holidayForm.end} onChange={(e) => setHolidayForm({ ...holidayForm, end: e.target.value })} />
                {holidayForm.end && <p className="text-[11px] text-zinc-400 font-semibold mt-1">{fmtUK(holidayForm.end)}</p>}
              </div>
            </div>
            <button onClick={() => subscribed ? addHoliday() : toast.error("Subscribe to add holidays")} data-testid="parent-holiday-add"
              className="nb-btn w-full bg-[#F9A8D4] py-2 text-sm"><Plus className="w-4 h-4" /> Add holiday</button>
          </div>
        </div>

        <div className="nb-card p-5" data-testid="parent-exams-card">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><GraduationCap className="w-5 h-5" /> Exams</h2>
          <p className="text-xs text-zinc-500 font-semibold mt-1">Add {yourLC} exam dates so they appear on the calendar.</p>
          <div className="mt-3 space-y-2" data-testid="parent-exams-list">
            {exams.map((e) => (
              <div key={e.id} className="rounded-xl border-2 border-zinc-900 p-2.5 bg-[#FDFBF7] flex items-start justify-between gap-2" data-testid={`parent-exam-${e.id}`}>
                <div className="min-w-0">
                  <div className="font-bold text-sm">{e.title}</div>
                  <div className="text-[11px] text-zinc-500 font-semibold">
                    {new Date(e.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}{e.start_time ? ` · ${e.start_time}` : ""}
                  </div>
                  {e.notes && <div className="text-xs text-zinc-500 mt-1">{e.notes}</div>}
                </div>
                <button onClick={() => deleteExam(e.id)} data-testid={`parent-exam-delete-${e.id}`}
                  className="p-1.5 shrink-0 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                  <Trash2 className="w-3.5 h-3.5 text-red-700" />
                </button>
              </div>
            ))}
            {exams.length === 0 && <p className="text-sm text-zinc-400 font-semibold" data-testid="parent-exams-empty">No exams added yet.</p>}
          </div>
          <div className="mt-3 pt-3 border-t-2 border-zinc-100 space-y-2">
            <input className="nb-input" placeholder="Exam name, e.g. English prelim" data-testid="parent-exam-title"
              value={examForm.title} onChange={(e) => setExamForm({ ...examForm, title: e.target.value })} maxLength={100} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input type="date" className="nb-input" data-testid="parent-exam-date"
                  value={examForm.date} onChange={(e) => { setExamForm({ ...examForm, date: e.target.value }); setExamPast(false); }} />
                {examForm.date && <p className="text-[11px] text-zinc-400 font-semibold mt-1">{fmtUK(examForm.date)}</p>}
              </div>
              <input type="time" className="nb-input" data-testid="parent-exam-time"
                value={examForm.start_time} onChange={(e) => setExamForm({ ...examForm, start_time: e.target.value })} />
            </div>
            <input className="nb-input" placeholder="Notes (optional)" data-testid="parent-exam-notes"
              value={examForm.notes} onChange={(e) => setExamForm({ ...examForm, notes: e.target.value })} maxLength={200} />
            {examPast && (
              <p className="text-xs font-bold text-amber-800 bg-amber-100 border-2 border-amber-300 rounded-lg p-2" data-testid="parent-exam-past-warning">
                That date is in the past — press "Add anyway" to save it (handy for recording mocks that already happened).
              </p>
            )}
            <button onClick={addExam} data-testid="parent-exam-add" className="nb-btn w-full bg-[#C7D2FE] py-2 text-sm">
              <GraduationCap className="w-4 h-4" /> {examPast ? "Add anyway" : "Add exam"}
            </button>
          </div>
        </div>

        <div className="nb-card p-5" data-testid="parent-year-groups-card">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><Settings2 className="w-5 h-5" /> Year groups</h2>
          <p className="text-xs text-zinc-500 font-semibold mt-1">Naming varies across the UK — pick a preset or edit your own list.</p>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="parent-year-preset-row">
            {Object.entries(YEAR_PRESETS).map(([k, v]) => (
              <button key={k} type="button" data-testid={`parent-year-preset-${k}`} onClick={() => subscribed ? setYearGroupsDraft([...v]) : toast.error("Subscribe to edit year groups")}
                className="nb-btn bg-white px-3 py-1.5 text-xs">{PRESET_LABELS[k]}</button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="parent-year-chips">
            {yearGroupsDraft.map((y) => (
              <span key={y} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-zinc-900 bg-[#A7E4D3] text-sm font-bold" data-testid={`parent-year-chip-${y.replace(/\s+/g, "-")}`}>
                {y}
                <button type="button" onClick={() => setYearGroupsDraft(yearGroupsDraft.filter((x) => x !== y))} data-testid={`parent-year-remove-${y.replace(/\s+/g, "-")}`}
                  className="hover:text-red-600 transition-colors duration-150"><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input className="nb-input flex-1" placeholder="e.g. Year 12" data-testid="parent-new-year-input"
              value={newYear} onChange={(e) => setNewYear(e.target.value)} />
            <button type="button" data-testid="parent-add-year-btn"
              onClick={() => { if (newYear.trim() && !yearGroupsDraft.includes(newYear.trim())) setYearGroupsDraft([...yearGroupsDraft, newYear.trim()]); setNewYear(""); }}
              className="nb-btn bg-[#FDE68A] px-4 py-2 text-sm"><Plus className="w-4 h-4" /> Add</button>
          </div>
          <button onClick={() => subscribed ? saveYearGroups() : toast.error("Subscribe to edit year groups")} data-testid="parent-year-groups-save"
            className="nb-btn w-full bg-[#A7E4D3] py-2 mt-3 text-sm"><Check className="w-4 h-4" /> Save year groups</button>
        </div>

        <div className="nb-card p-5" data-testid="parent-advance-card">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><ArrowUp className="w-5 h-5" /> New school year</h2>
          <p className="text-xs text-zinc-500 font-semibold mt-1">After summer, move {isStudent ? "up a year" : `${child.name} up a year`} — with the option to clear and rebuild the timetable.</p>
          <label className="nb-label mt-4" htmlFor="parent-child-year-set">Current year group</label>
          <div className="flex gap-2 mt-2">
            <select id="parent-child-year-set" className="nb-input flex-1" data-testid="parent-child-year-set"
              value={childYear} onChange={(e) => setChildYear(e.target.value)}>
              <option value="">Choose…</option>
              {(child.year_groups || []).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button onClick={() => subscribed ? saveChildYear() : toast.error("Subscribe to save")} data-testid="parent-save-year-btn"
              className="nb-btn bg-white px-4 py-2 text-sm shrink-0">Save</button>
          </div>
          <label className="flex items-start gap-2 mt-4 font-bold text-sm cursor-pointer">
            <input type="checkbox" data-testid="parent-advance-clear" checked={clearOnAdvance} onChange={(e) => setClearOnAdvance(e.target.checked)} className="w-4 h-4 mt-0.5 accent-zinc-900" />
            Clear timetable & notes for a fresh start
          </label>
          <button onClick={() => subscribed ? advance() : toast.error("Subscribe to advance")} disabled={!childYear} data-testid="parent-advance-btn"
            className="nb-btn w-full bg-[#D8B4FE] py-2.5 mt-3 text-sm">
            <ArrowUp className="w-4 h-4" /> Advance to next year
          </button>
        </div>
      </div>
    </div>
  );
}
